<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEvents } from '@/composables/useEvents'
import { useEditions } from '@/composables/useEditions'
import type { Edition } from '@/types/events'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import { parseDeletionBlocked, deletionBlockedMessageKey } from '@/utils/deletionBlocked'
import OverflowMenu from '@/components/shared/OverflowMenu.vue'
import HeaderCreateButton from '@/components/shared/HeaderCreateButton.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import PayoutNotReadyNotice from '@/components/organizer/payouts/PayoutNotReadyNotice.vue'
import { payoutNotReadyMessage } from '@/api/payouts'

const { t } = useI18n()
const route = useRoute()
const { event, loading: eventLoading, fetchEvent } = useEvents()
const { editions, loading: editionsLoading, fetchEditions, activateEdition, deleteEdition } = useEditions()

const confirmDeleteEditionSlug = ref<string | null>(null)
const confirmDeleteEditionName = ref('')
const actionLoading = ref(false)
const deleteBlockedMessage = ref<string | null>(null)
const successMessage = ref('')
const payoutNotReady = ref<string | null>(null)

const eventSlug = computed(() => route.params['eventSlug'] as string)

watch(eventSlug, async (slug) => {
  await Promise.all([fetchEvent(slug), fetchEditions(slug)])
}, { immediate: true })


function requestDeleteEdition(ed: Edition): void {
  confirmDeleteEditionSlug.value = ed.slug
  confirmDeleteEditionName.value = ed.name
  successMessage.value = ''
  deleteBlockedMessage.value = null
}

function cancelDeleteEdition(): void {
  deleteBlockedMessage.value = null
  confirmDeleteEditionSlug.value = null
  confirmDeleteEditionName.value = ''
}

async function confirmDeleteEdition(): Promise<void> {
  if (confirmDeleteEditionSlug.value === null) return
  actionLoading.value = true
  deleteBlockedMessage.value = null
  try {
    await deleteEdition(eventSlug.value, confirmDeleteEditionSlug.value)
    successMessage.value = t('editions.list.delete_success')
    confirmDeleteEditionSlug.value = null
    confirmDeleteEditionName.value = ''
  } catch (err: unknown) {
    const blocked = parseDeletionBlocked(err)
    if (blocked === null) throw err
    deleteBlockedMessage.value = t(deletionBlockedMessageKey(blocked.subject))
  } finally {
    actionLoading.value = false
  }
}

async function handleActivateEdition(editionSlug: string): Promise<void> {
  actionLoading.value = true
  successMessage.value = ''
  payoutNotReady.value = null
  try {
    await activateEdition(eventSlug.value, editionSlug)
    successMessage.value = t('editions.list.activate_success')
  } catch (err: unknown) {
    const message = payoutNotReadyMessage(err)
    if (message === null) throw err
    payoutNotReady.value = message
  } finally {
    actionLoading.value = false
  }
}

function formatDateRange(startDate: string, endDate: string): string {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
  const startStr = start.toLocaleDateString(undefined, opts)
  if (startDate === endDate) return startStr
  const endStr = end.toLocaleDateString(undefined, opts)
  return `${startStr} – ${endStr}`
}

function formatRevenue(value: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(Number(value))
}
</script>

<template>
  <OrganizerLayout>

    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- Breadcrumb -->
      <nav aria-label="breadcrumb" class="flex items-center gap-2 text-xs text-slate-500 mb-6 flex-wrap">
        <RouterLink to="/organizer/dashboard" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.dashboard') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <RouterLink to="/organizer/events" class="hover:text-slate-300 transition-colors">
          {{ t('nav.sidebar.events') }}
        </RouterLink>
        <span aria-hidden="true">/</span>
        <span class="text-slate-300">
          <template v-if="event">{{ event.name }}</template>
          <span v-else class="text-slate-600">…</span>
        </span>
      </nav>

      <!-- Page heading -->
      <div class="flex items-center justify-between mb-8">
        <div>
          <h1 class="text-3xl font-bold text-white">
            <template v-if="event">{{ event.name }}</template>
            <span v-else class="text-slate-600">…</span>
          </h1>
          <p class="text-slate-400 mt-1">{{ t('events.detail.editions_heading') }}</p>
          <span
            v-if="event?.maintenance"
            class="inline-flex items-center rounded-full bg-amber-500/15 border border-amber-500/30 px-2.5 py-0.5 text-xs font-medium text-amber-400"
          >
            {{ t('events.list.maintenance_badge') }}
          </span>
        </div>
        <HeaderCreateButton
          v-if="event"
          :to="`/organizer/events/${eventSlug}/editions/create`"
          :label="t('events.detail.create_edition_button')"
        />
      </div>

      <!-- Loading state -->
      <AppSpinner v-if="eventLoading" />

      <template v-else-if="event">

        <!-- Publish blocked: payouts not set up (issue #237) -->
        <PayoutNotReadyNotice v-if="payoutNotReady" :message="payoutNotReady" />

        <!-- Success message -->
        <div
          v-if="successMessage"
          class="mb-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-5 py-3 text-sm text-emerald-400"
        >
          {{ successMessage }}
        </div>

        <!-- Loading editions -->
        <div v-if="editionsLoading" class="flex items-center justify-center py-8">
          <svg class="animate-spin h-6 w-6 text-emerald-500" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>

        <!-- Empty editions state -->
        <div
          v-else-if="editions.length === 0"
          class="rounded-xl border border-dashed border-slate-700 px-8 py-12 text-center"
        >
          <p class="text-slate-400 text-sm">{{ t('events.detail.no_editions') }}</p>
        </div>

        <!-- Editions list -->
        <div v-else class="space-y-3">
          <article
            v-for="ed in editions"
            :key="ed.id"
            class="rounded-xl bg-slate-800/50 border border-slate-700 px-5 py-4 hover:border-slate-600 transition-colors"
          >
            <div class="flex items-start gap-4">
              <!-- Edition info -->
              <div class="flex-1 min-w-0">
                <!-- Name row + status badge -->
                <div class="flex items-center gap-2 flex-wrap">
                  <RouterLink
                    :to="`/organizer/events/${eventSlug}/editions/${ed.slug}`"
                    class="text-sm font-semibold text-white hover:text-emerald-400 transition-colors"
                  >
                    {{ ed.name }}
                  </RouterLink>
                  <span
                    :class="ed.active
                      ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                      : 'bg-slate-700 border-slate-600 text-slate-400'"
                    class="shrink-0 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium"
                  >
                    {{ ed.active ? t('editions.list.active_badge') : t('editions.list.inactive_badge') }}
                  </span>
                </div>

                <!-- Date range + location -->
                <p class="text-xs text-slate-400 mt-3">
                  {{ formatDateRange(ed.start_date, ed.end_date) }}
                  <template v-if="ed.venue?.name">
                    <span class="mx-1">&middot;</span>
                    {{ ed.venue.name }}
                  </template>
                </p>

                <!-- Stats row -->
                <div class="flex items-center gap-4 mt-2">
                  <span class="inline-flex items-center gap-1 text-xs text-slate-400">
                    {{ ed.participant_count }} {{ t('editions.list.stat_participants') }}
                    <span class="mx-1">&middot;</span>
                    {{ formatRevenue(ed.total_revenue) }} {{ t('editions.list.stat_revenue') }}
                  </span>
                </div>
              </div>

              <!-- Actions -->
              <div class="shrink-0 flex items-center gap-2">
                <RouterLink
                  :to="`/organizer/events/${eventSlug}/editions/${ed.slug}/participants`"
                  class="hidden sm:inline-flex text-xs font-medium text-blue-400 hover:text-blue-300 transition-colors px-3 py-1.5 rounded-lg border border-blue-500/30 hover:border-blue-500/60"
                >
                  {{ t('nav.participants') }}
                </RouterLink>
                <RouterLink
                  :to="`/organizer/events/${eventSlug}/editions/${ed.slug}`"
                  class="hidden sm:inline-flex text-xs font-medium text-emerald-400 hover:text-emerald-300 transition-colors px-3 py-1.5 rounded-lg border border-emerald-500/30 hover:border-emerald-500/60"
                >
                  {{ t('editions.list.action_view') }}
                </RouterLink>
                <button
                  v-if="!ed.active"
                  type="button"
                  :disabled="actionLoading"
                  class="hidden sm:inline-flex text-xs font-medium text-emerald-400 hover:text-emerald-300 transition-colors px-3 py-1.5 rounded-lg border border-emerald-500/30 hover:border-emerald-500/60 disabled:opacity-50"
                  @click="handleActivateEdition(ed.slug)"
                >
                  {{ t('editions.list.action_activate') }}
                </button>

                <!-- Overflow menu -->
                <OverflowMenu>
                  <!-- Mobile-only: below sm, these detached action buttons are hidden, so
                       they are duplicated here (sm:hidden) to keep the actions reachable. -->
                  <RouterLink
                    :to="`/organizer/events/${eventSlug}/editions/${ed.slug}/participants`"
                    class="sm:hidden w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors flex items-center"
                  >
                    {{ t('nav.participants') }}
                  </RouterLink>
                  <RouterLink
                    :to="`/organizer/events/${eventSlug}/editions/${ed.slug}`"
                    class="sm:hidden w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors flex items-center"
                  >
                    {{ t('editions.list.action_view') }}
                  </RouterLink>
                  <button
                    v-if="!ed.active"
                    type="button"
                    :disabled="actionLoading"
                    class="sm:hidden w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors disabled:opacity-50"
                    @click="handleActivateEdition(ed.slug)"
                  >
                    {{ t('editions.list.action_activate') }}
                  </button>
                  <hr class="sm:hidden border-slate-700 my-1" />

                  <RouterLink
                    :to="`/organizer/events/${eventSlug}/editions/${ed.slug}/settings`"
                    class="w-full text-left px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors flex items-center"
                  >
                    {{ t('editions.list.action_edit') }}
                  </RouterLink>
                  <hr class="border-slate-700 my-1" />
                  <button
                    type="button"
                    class="w-full text-left px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-slate-700/50 transition-colors"
                    @click="requestDeleteEdition(ed)"
                  >
                    {{ t('editions.list.action_delete') }}
                  </button>
                </OverflowMenu>
              </div>
            </div>
          </article>
        </div>
      </template>

      <ConfirmDialog
        :open="confirmDeleteEditionSlug !== null"
        :title="t('editions.list.confirm_delete_title')"
        :body="t('editions.list.confirm_delete_body', { name: confirmDeleteEditionName })"
        :confirm-label="t('editions.list.confirm_delete_confirm')"
        :loading="actionLoading"
        @update:open="cancelDeleteEdition"
        @confirm="confirmDeleteEdition"
      >
        <template v-if="deleteBlockedMessage" #warning>
          {{ deleteBlockedMessage }}
        </template>
      </ConfirmDialog>
    </main>
  </OrganizerLayout>
</template>
